import React, { createContext, useContext, useState, useEffect } from 'react';
import useApi from '../hooks/useApi';

const AuthContext = createContext();

// Auth context provider component
export const AuthProvider = ({ children }) => {
  // Keep user data in memory and persist essential data in localStorage for refresh handling
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null); // Separate state for user ID (not stored in localStorage)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState(null);

  const apiHook = useApi();

  // Function to check application status
  const checkApplicationStatus = async (userId) => {
    if (!userId) return;

    setIsCheckingStatus(true);
    try {
      const statusResp = await apiHook.get(`/vendor/user/${userId}/status`);
      const status = (statusResp?.status || statusResp?.application_status || '').toLowerCase();
      setApplicationStatus(status);
      
      // Store confirmation number if available in response
      if (statusResp?.confirmation_number) {
        setConfirmationNumberValue(statusResp.confirmation_number);
      }
    } catch (error) {
      // If it's a 401 error, the user might not be authenticated properly
      if (error.message.includes('401')) {
        setIsAuthenticated(false);
        setUser(null);
      }
      setApplicationStatus('error');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Check authentication status on mount - clear everything on refresh
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Clear all localStorage on page refresh
      localStorage.clear();
      
      // Clear all cookies by setting them to expire in the past
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        }
      }
      
      // Reset all states
      setUser(null);
      setUserId(null);
      setIsAuthenticated(false);
      setApplicationStatus(null);
      setIsCheckingStatus(false);
      setConfirmationNumber(null);
      
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

    // Login function - no longer stores access token in localStorage
  const login = async (userData) => {
    // Extract user ID and store separately (not in localStorage)
    const { id, ...userDataWithoutId } = userData;
    setUserId(id);
    setUser(userDataWithoutId);
    
    if (userDataWithoutId) {
      localStorage.setItem('user_data', JSON.stringify(userDataWithoutId));
      // Check application status after login with a small delay to ensure cookie is set
      if (id) {
        // Add a small delay to ensure the HTTP-only cookie is properly set
        setTimeout(async () => {
          await checkApplicationStatus(id);
        }, 100);
      }
    }
    
    // Load confirmation number from localStorage if available
    const storedConfirmationNumber = localStorage.getItem('confirmation_number');
    if (storedConfirmationNumber) {
      setConfirmationNumber(storedConfirmationNumber);
    }
    
    // Only set as authenticated if we have both user data and userId
    setIsAuthenticated(!!(userDataWithoutId && id));
  };

  // Logout function - makes request to logout endpoint to clear cookie
  const logout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL}/auth/logout-web`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
    }
    
    localStorage.removeItem('user_data');
    localStorage.removeItem('vreels_user'); // cleanup legacy storage
    localStorage.removeItem('confirmation_number'); // clear confirmation number
    setUser(null);
    setUserId(null);
    setIsAuthenticated(false);
    setApplicationStatus(null);
    setIsCheckingStatus(false);
    setConfirmationNumber(null);
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(prev => {
      const updatedUser = { ...prev, ...userData };
      if (updatedUser) {
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
      }
      return updatedUser;
    });
  };

  // Update user ID separately
  const updateUserId = (id) => {
    setUserId(id);
  };

  // Set confirmation number
  const setConfirmationNumberValue = (number) => {
    setConfirmationNumber(number);
    if (number) {
      localStorage.setItem('confirmation_number', number);
    } else {
      localStorage.removeItem('confirmation_number');
    }
  };

  const value = {
    user,
    userId,
    isAuthenticated,
    isLoading,
    applicationStatus,
    isCheckingStatus,
    confirmationNumber,
    login,
    logout,
    updateUser,
    updateUserId,
    checkApplicationStatus,
    setConfirmationNumberValue
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
